import React, { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@axaou/ui';
import { useRecoilState, useRecoilValue } from 'recoil';
import styled from 'styled-components';

import { OverviewManhattan } from './OverviewManhattan';
import { UnifiedLocusTable } from './UnifiedLocusTable';
import type { UnifiedOverviewResponse, UnifiedLocus } from './types';
import type { PeakLabelNode } from './hooks/usePeakLabelLayout';
import { axaouDevUrl, pouchDbName, cacheEnabled } from '../Query';
import { ancestryGroupAtom, selectedContigAtom } from '../sharedState';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { useLocalStorage, useLocalStorageSet } from '../hooks/useLocalStorage';
import { configQuery } from '../queryStates';
import { getOverviewVariantNavigationState } from './overviewVariantNavigation';
import {
  getPhenotypeLocusNavigationDecision,
  type PhenotypeLocusNavigationDecision,
} from './locusNavigationPolicy';

const Container = styled.div`
  width: 100%;
  margin-top: 20px;
`;

const Y_AXIS_WIDTH = 50;

export interface OverviewPlotContainerProps {
  analysisId: string;
  /** Callback when a locus is clicked (for zoom navigation) */
  onLocusClick?: (contig: string, position: number, start?: number, stop?: number) => void;
}

/**
 * Container component that fetches unified overview data and renders
 * the OverviewManhattan and UnifiedLocusTable components.
 */
export const OverviewPlotContainer: React.FC<OverviewPlotContainerProps> = ({
  analysisId,
  onLocusClick,
}) => {
  const ancestryGroup = useRecoilValue(ancestryGroupAtom);
  const [selectedContig, setSelectedContig] = useRecoilState(selectedContigAtom);
  const { goToGene, goToRegion, goToVariant, getNewTabUrl } = useAppNavigation();
  const configState = useRecoilValue(configQuery);
  // Prefer build-time env var, fall back to runtime config
  const dataVersion = (typeof process !== 'undefined' && process.env?.DATA_VERSION) || configState.data?.data_version || '';

  const baseStorageKey = `overview_${analysisId}_${ancestryGroup}_${selectedContig}`;

  // State for peak selection (shared between plot and table)
  const [selectedPeakIds, setSelectedPeakIds] = useLocalStorageSet(`${baseStorageKey}_peaks`, new Set());
  const [customLabelMode, setCustomLabelMode] = useLocalStorage(`${baseStorageKey}_mode`, false);
  const [topN, setTopN] = useLocalStorage(`${baseStorageKey}_topN`, 10);
  const [hideSingletons, setHideSingletons] = useState(false);
  const [minNonCodingVariants, setMinNonCodingVariants] = useState(() => {
    const stored = localStorage.getItem('minNonCodingVariants');
    return stored != null ? Number(stored) : 3;
  });
  const updateMinNonCodingVariants = useCallback((v: number) => {
    setMinNonCodingVariants(v);
    localStorage.setItem('minNonCodingVariants', String(v));
  }, []);

  interface Data {
    overviewData: UnifiedOverviewResponse | null;
  }

  const { queryStates, anyLoading } = useQuery<Data>({
    dbName: pouchDbName,
    queries: [
      {
        url: `${axaouDevUrl}/phenotype/${analysisId}/overview?ancestry=${ancestryGroup}&v=${dataVersion}`,
        name: 'overviewData',
      },
    ],
    deps: [analysisId, ancestryGroup, dataVersion],
    cacheEnabled,
  });

  const clearSelection = useCallback(() => {
    setTopN(0);
    setSelectedPeakIds(new Set());
    setCustomLabelMode(false);
  }, []);

  const resetToDefault = useCallback(() => {
    setSelectedPeakIds(new Set());
    setCustomLabelMode(false);
  }, []);

  const handleSetTopN = useCallback((n: number) => {
    setTopN(n);
    setSelectedPeakIds(new Set());
    setCustomLabelMode(false);
  }, []);

  const handlePolicyNavigation = useCallback((decision: PhenotypeLocusNavigationDecision) => {
    if (decision.kind === 'gene' && decision.state.geneId) {
      goToGene(decision.state.geneId, {
        fromPhenotype: true,
        analysisId: decision.state.analysisId,
      });
    } else if (decision.state.regionId) {
      goToRegion(decision.state.regionId, { fromPhenotype: true });
    }
  }, [goToGene, goToRegion]);

  const handleVariantClick = useCallback((
    variantId: string,
    geneId: string,
    regionId: string
  ) => {
    goToVariant(variantId, {
      geneId,
      regionId,
      analysisId,
      resultIndex: 'variant-phewas',
    });
  }, [goToVariant, analysisId]);

  const getVariantHref = useCallback((
    variantId: string,
    geneId: string,
    locus: UnifiedLocus
  ) => getNewTabUrl(
    getOverviewVariantNavigationState({ variantId, geneId, analysisId, locus }),
    { destination: 'results' }
  ), [getNewTabUrl, analysisId]);

  const handleLocusClick = useCallback(
    (contig: string, position: number, start?: number, stop?: number) => {
      onLocusClick?.(contig, position, start, stop);
    },
    [onLocusClick]
  );

  // Get data reference (may be null during loading)
  const data = queryStates.overviewData?.data ?? null;

  // Filter loci by selected chromosome (must be called unconditionally for hooks rules)
  const contigFilteredLoci = useMemo(() => {
    if (!data) return [];
    if (selectedContig === 'all') return data.unified_loci;
    // Normalize to match backend format which generally uses 'chrN'
    const targetContig = selectedContig.startsWith('chr')
      ? selectedContig
      : `chr${selectedContig}`;
    return data.unified_loci.filter((l) => l.contig === targetContig);
  }, [data, selectedContig]);

  // Apply singleton filter (shared between plot and table)
  // Only filter singletons in genome-wide view; in chromosome view show all peaks
  const filteredLoci = useMemo(() => {
    if (!hideSingletons || selectedContig !== 'all') return contigFilteredLoci;
    return contigFilteredLoci.filter((locus) =>
      !locus.sig_variant_count || locus.sig_variant_count >= 3 ||
      locus.genes.some((g) => {
        const hasBurden = (g.burden_results ?? []).some((b) =>
          ((b.pvalue ?? Infinity) < 2.5e-6) ||
          ((b.pvalue_burden ?? Infinity) < 2.5e-6) ||
          ((b.pvalue_skat ?? Infinity) < 2.5e-6)
        );
        const coding = (g.genome_coding_hits?.lof ?? 0) + (g.exome_coding_hits?.lof ?? 0) +
          (g.genome_coding_hits?.missense ?? 0) + (g.exome_coding_hits?.missense ?? 0);
        return hasBurden || coding > 0;
      })
    );
  }, [contigFilteredLoci, hideSingletons, selectedContig]);

  // Apply min non-coding variants filter (loci with coding variants or burden always pass)
  const minVarFilteredLoci = useMemo(() => {
    if (minNonCodingVariants <= 0) return filteredLoci;
    return filteredLoci.filter((locus) => {
      const isBurdenOnly = locus.pvalue_genome == null && locus.pvalue_exome == null;
      if (isBurdenOnly) return true;
      const hasCoding = locus.genes.some((g) => {
        const lof = (g.genome_coding_hits?.lof ?? 0) + (g.exome_coding_hits?.lof ?? 0);
        const mis = (g.genome_coding_hits?.missense ?? 0) + (g.exome_coding_hits?.missense ?? 0);
        return lof > 0 || mis > 0;
      });
      if (hasCoding) return true;
      return locus.sig_variant_count >= minNonCodingVariants;
    });
  }, [filteredLoci, minNonCodingVariants]);

  const locusById = useMemo(
    () => new Map(minVarFilteredLoci.map((locus) => [locus.locus_id, locus])),
    [minVarFilteredLoci]
  );

  const getPeakNavigationDecision = useCallback((node: PeakLabelNode) => {
    const locus = locusById.get(node.peak.locus_id);
    if (!locus) return null;
    return getPhenotypeLocusNavigationDecision({
      analysisId,
      locus,
      interaction: { kind: 'peak' },
    });
  }, [analysisId, locusById]);

  const handlePeakClick = useCallback((node: PeakLabelNode) => {
    const decision = getPeakNavigationDecision(node);
    if (decision) handlePolicyNavigation(decision);
  }, [getPeakNavigationDecision, handlePolicyNavigation]);

  const getPeakDestinationTitle = useCallback(
    (node: PeakLabelNode) => getPeakNavigationDecision(node)?.tooltip,
    [getPeakNavigationDecision]
  );

  // Compute the default labeled IDs (mirrors the hook's implicated-first sort)
  // Used when transitioning from default→custom mode so the base set matches what's displayed
  const SIG_THRESHOLD = 2.5e-6;
  const defaultLabeledIds = useMemo(() => {
    if (customLabelMode) return selectedPeakIds;
    const isImplicated = (l: UnifiedLocus): boolean =>
      l.genes.some((g) => {
        const hasBurden = g.burden_results?.some((b) =>
          ((b.pvalue ?? Infinity) < SIG_THRESHOLD) ||
          ((b.pvalue_burden ?? Infinity) < SIG_THRESHOLD) ||
          ((b.pvalue_skat ?? Infinity) < SIG_THRESHOLD)
        );
        const hasCoding =
          ((g.genome_coding_hits?.lof ?? 0) + (g.exome_coding_hits?.lof ?? 0)) > 0 ||
          ((g.genome_coding_hits?.missense ?? 0) + (g.exome_coding_hits?.missense ?? 0)) > 0;
        return hasBurden || hasCoding;
      });
    const sorted = [...minVarFilteredLoci].sort((a, b) => {
      const aImpl = isImplicated(a);
      const bImpl = isImplicated(b);
      if (aImpl !== bImpl) return aImpl ? -1 : 1;
      const bestA = Math.min(a.pvalue_genome ?? Infinity, a.pvalue_exome ?? Infinity);
      const bestB = Math.min(b.pvalue_genome ?? Infinity, b.pvalue_exome ?? Infinity);
      return bestA - bestB;
    });
    // Auto-select: implicated loci first (up to 25), then fill remaining slots with top GWAS peaks
    const effectiveLimit = topN === 10 ? 25 : topN;
    const implicated = sorted.filter(l => isImplicated(l)).slice(0, effectiveLimit);
    const implicatedIds = new Set(implicated.map(l => `${l.contig}-${l.position}`));
    // Fill remaining slots with non-implicated GWAS peaks (sorted by p-value already)
    const remaining = sorted.filter(l => !implicatedIds.has(`${l.contig}-${l.position}`));
    const filled = [...implicated, ...remaining].slice(0, effectiveLimit);
    return new Set(filled.map((l) => `${l.contig}-${l.position}`));
  }, [minVarFilteredLoci, topN, customLabelMode, selectedPeakIds]);

  // Toggle a peak label on/off. When transitioning from default→custom mode,
  // initializes from currentLabeledIds (from plot nodes) or defaultLabeledIds (for table).
  const togglePeak = useCallback((peakId: string, currentLabeledIds?: Set<string>) => {
    setSelectedPeakIds((prev) => {
      const baseSet = customLabelMode ? prev : (currentLabeledIds ?? defaultLabeledIds);
      const next = new Set(baseSet);
      if (next.has(peakId)) {
        next.delete(peakId);
      } else {
        next.add(peakId);
      }
      return next;
    });
    setCustomLabelMode(true);
  }, [customLabelMode, defaultLabeledIds]);

  // Construct full image URLs with optional contig parameter and data version for cache-busting
  const contigParam = selectedContig !== 'all' ? `&contig=${selectedContig}` : '';
  const versionParam = `&v=${dataVersion}`;

  const genomeImageUrl = useMemo(() => {
    if (!data) return '';
    const separator = data.genome_image_url.includes('?') ? '' : '?';
    const baseUrl = data.genome_image_url.startsWith('/')
      ? `${axaouDevUrl.replace('/api', '')}${data.genome_image_url}`
      : data.genome_image_url;
    return `${baseUrl}${separator}${contigParam}${versionParam}`;
  }, [data, contigParam, dataVersion]);

  const exomeImageUrl = useMemo(() => {
    if (!data) return '';
    const separator = data.exome_image_url.includes('?') ? '' : '?';
    const baseUrl = data.exome_image_url.startsWith('/')
      ? `${axaouDevUrl.replace('/api', '')}${data.exome_image_url}`
      : data.exome_image_url;
    return `${baseUrl}${separator}${contigParam}${versionParam}`;
  }, [data, contigParam, dataVersion]);

  // Hide entirely while loading (avoids layout shift)
  if (anyLoading()) {
    return null;
  }

  // Show message if data isn't available
  if (queryStates.overviewData?.error || !data) {
    return (
      <Container>
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          <p>Overview data is not available for this phenotype.</p>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <OverviewManhattan
        storageKey={baseStorageKey}
        genomeImageUrl={genomeImageUrl}
        exomeImageUrl={exomeImageUrl}
        unifiedLoci={minVarFilteredLoci}
        selectedPeakIds={selectedPeakIds}
        customLabelMode={customLabelMode}
        topN={topN}
        onPeakClick={handlePeakClick}
        getPeakDestinationTitle={getPeakDestinationTitle}
        onPeakToggle={(peakId, currentLabeledIds) => togglePeak(peakId, currentLabeledIds)}
        showYAxis={true}
        contig={selectedContig}
        onResetContig={() => setSelectedContig('all')}
        onContigClick={setSelectedContig}
      />

      {minVarFilteredLoci.length > 0 && (
        <div style={{ marginLeft: Y_AXIS_WIDTH }}>
          <UnifiedLocusTable
            unifiedLoci={minVarFilteredLoci}
            onLocusClick={handleLocusClick}
            onNavigate={handlePolicyNavigation}
            analysisId={analysisId}
            onVariantClick={handleVariantClick}
            getVariantHref={getVariantHref}
            selectedPeakIds={selectedPeakIds}
            onTogglePeak={togglePeak}
            customLabelMode={customLabelMode}
            topN={topN}
            onSetTopN={handleSetTopN}
            onClearSelection={clearSelection}
            onResetToDefault={resetToDefault}
            hideSingletons={hideSingletons}
            onSetHideSingletons={setHideSingletons}
            minNonCodingVariants={minNonCodingVariants}
            onSetMinNonCodingVariants={updateMinNonCodingVariants}
          />
        </div>
      )}
    </Container>
  );
};

export default OverviewPlotContainer;
